import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useForm } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui';
import { useEventTags } from 'src/hooks/api/useEventTags';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { DetailSection } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { tagHasChanges } from 'src/utils/tagUtils';
import { ActionOutboxEntityType, Permission, Tag } from 'src/WebApiController';

import { CancelButton } from '../../../Buttons';
import { TagsFormBody } from '../../../TagsFormBody';
import * as styles from '../InventoryEventPage.css';

type TagsUpdateInput = {
  tags: Tag[];
};

export const TagsSection = ({
  eventId,
  disabled,
}: {
  eventId: string;
  disabled?: boolean;
}) => {
  const { loginContext } = useAppContext();
  const hasUpdateTagsPermission = useUserHasAnyOfPermissions(
    Permission.Events_UpdateTags
  );

  const isNotConsignee = useMemo(
    () =>
      loginContext?.user?.activeAccount != null &&
      loginContext.user.activeAccount.isConsignee == false,
    [loginContext]
  );

  const canEditTags = isNotConsignee && hasUpdateTagsPermission;

  const { setValue, watch, handleSubmit } = useForm<TagsUpdateInput>();
  const tags = watch('tags') ?? [];
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    tags: existingTags,
    updateTags,
    isLoading,
  } = useEventTags(eventId, disabled);

  const { tagsMetadata } = useTagsForEntityType(
    ActionOutboxEntityType.SellerEvent
  );

  const onSubmitHandler = useCallback(
    async (data: TagsUpdateInput) => {
      setIsSubmitting(true);
      await updateTags(data.tags, eventId);
      setIsSubmitting(false);
    },
    [eventId, updateTags]
  );

  useEffect(() => {
    if (existingTags && !isEqual(tags, existingTags)) {
      setValue('tags', existingTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingTags]);

  const hasChanges = tagHasChanges(tags, existingTags, tagsMetadata ?? []);

  if (isLoading) {
    return <PosSpinner />;
  }

  return (
    <div className={styles.tableSection} style={{ maxWidth: '768px' }}>
      <DetailSection name={<Content id={ContentId.Tags} />}>
        <TagsFormBody
          canEditTags={canEditTags}
          tagsCurrentEntity={tags}
          onTagsUpdate={(tags) => {
            setValue('tags', tags);
          }}
          disabled={disabled || isSubmitting}
          entityType={ActionOutboxEntityType.SellerEvent}
        />

        {hasChanges && (
          <div className={styles.formFooter}>
            <Button
              disabled={isSubmitting || disabled}
              onClick={() => {
                handleSubmit(onSubmitHandler)();
              }}
            >
              <Content id={ContentId.Save} />
            </Button>

            <CancelButton
              onClick={() => setValue('tags', existingTags ?? [])}
              disabled={isSubmitting || disabled}
              textContentId={ContentId.Cancel}
            />
          </div>
        )}
      </DetailSection>
    </div>
  );
};
